<template>
  <div
    class="wcag-modal"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('Components.WcagReportModal.title')"
  >
    <button
      class="wcag-modal__close"
      @click="onClose"
      :aria-label="$t('Views.Stage.ParticipantsModal.close')"
    >
      <i class="fas fa-times"></i>
    </button>

    <!-- Header -->
    <div class="wcag-modal__header">
      <div class="wcag-modal__icon-wrap">
        <i class="fas fa-universal-access"></i>
      </div>
      <h2 class="wcag-modal__title">
        {{ $t('Components.WcagReportModal.title') }}
      </h2>
      <p class="wcag-modal__sub">
        {{ $t('Components.WcagReportModal.subtitle') }}
      </p>
    </div>

    <!-- Disclaimer banner — sits right under the header so it's the
         FIRST thing the reader sees, before any score. The hand-rolled
         checker this report used to ship with implied conformance; an
         automated tool can never make that claim. -->
    <div class="wcag-modal__disclaimer" role="note">
      <i
        class="fas fa-info-circle wcag-modal__disclaimer-icon"
        aria-hidden="true"
      ></i>
      <div class="wcag-modal__disclaimer-body">
        <strong>{{ $t('Components.WcagReportModal.disclaimerTitle') }}</strong>
        {{ $t('Components.WcagReportModal.disclaimerBody') }}
      </div>
    </div>

    <!-- Loading state — axe-core takes a moment to dynamic-import +
         scan the document. -->
    <div v-if="state === 'loading'" class="wcag-modal__loading">
      <i class="fas fa-circle-notch fa-spin"></i>
      <p>{{ $t('Components.WcagReportModal.running') }}</p>
    </div>

    <!-- Error state — axe-core couldn't load (usually a network
         issue since the engine is fetched from a CDN at runtime). -->
    <div v-else-if="state === 'error'" class="wcag-modal__error">
      <i class="fas fa-exclamation-triangle"></i>
      <p class="wcag-modal__error-title">
        {{ $t('Components.WcagReportModal.engineUnavailable') }}
      </p>
      <p class="wcag-modal__error-detail">{{ errorMessage }}</p>
      <i18n
        path="Components.WcagReportModal.errorHint"
        tag="p"
        class="wcag-modal__error-hint"
      >
        <template v-slot:tryAgain>
          <strong>{{ $t('Components.WcagReportModal.tryAgain') }}</strong>
        </template>
      </i18n>
      <button
        class="wcag-modal__btn wcag-modal__btn--secondary"
        @click="runAudit"
        style="margin-top: 14px"
      >
        <i class="fas fa-redo-alt"></i>
        {{ $t('Components.WcagReportModal.tryAgain') }}
      </button>
    </div>

    <!-- Results state — the audit ran and we have data. -->
    <template v-else-if="state === 'ready'">
      <!-- Score ring -->
      <div class="wcag-modal__score-section">
        <div class="wcag-modal__ring">
          <svg viewBox="0 0 120 120" class="wcag-modal__ring-svg">
            <circle
              cx="60"
              cy="60"
              r="52"
              fill="none"
              stroke="#e5e7eb"
              stroke-width="10"
            />
            <circle
              cx="60"
              cy="60"
              r="52"
              fill="none"
              :stroke="ringColor"
              stroke-width="10"
              stroke-linecap="round"
              :stroke-dasharray="circumference"
              :stroke-dashoffset="dashOffset"
              transform="rotate(-90 60 60)"
              class="wcag-modal__ring-progress"
            />
          </svg>
          <div class="wcag-modal__ring-label">
            <span class="wcag-modal__ring-num">{{ score }}</span>
            <span class="wcag-modal__ring-max">/ 100</span>
          </div>
        </div>
        <div class="wcag-modal__score-meta">
          <span
            class="wcag-modal__score-badge"
            :class="`wcag-modal__score-badge--${scoreBadge.tone}`"
          >
            <i :class="scoreBadge.icon"></i> {{ scoreBadge.label }}
          </span>
          <span class="wcag-modal__score-date">{{
            $t('Components.WcagReportModal.audited', { date: auditDate })
          }}</span>
        </div>
        <i18n
          path="Components.WcagReportModal.automatedChecks"
          tag="p"
          class="wcag-modal__score-caption"
        >
          <template v-slot:pass>
            <strong>{{ stats.pass }}</strong>
          </template>
          <template v-slot:fail>
            <strong>{{ stats.fail }}</strong>
          </template>
          <template v-slot:review>
            <strong>{{ stats.review }}</strong>
          </template>
        </i18n>
        <i18n
          v-if="auditedRoute"
          path="Components.WcagReportModal.scoreScopeWithRoute"
          tag="p"
          class="wcag-modal__score-caption wcag-modal__score-scope"
        >
          <template v-slot:route>
            <strong>{{ auditedRoute }}</strong>
          </template>
        </i18n>
        <p v-else class="wcag-modal__score-caption wcag-modal__score-scope">
          {{ $t('Components.WcagReportModal.scoreScope') }}
        </p>
      </div>

      <!-- Category breakdown — grouped by WCAG principle. -->
      <div class="wcag-modal__categories">
        <div v-for="cat in categories" :key="cat.name" class="wcag-cat">
          <div class="wcag-cat__header">
            <div class="wcag-cat__icon" :class="`wcag-cat__icon--${cat.color}`">
              <i :class="cat.icon" aria-hidden="true"></i>
            </div>
            <div class="wcag-cat__info">
              <div class="wcag-cat__name">
                {{ $t(`Components.WcagReportModal.principles.${cat.key}`) }}
              </div>
              <div class="wcag-cat__score">{{ cat.score }} / {{ cat.max }}</div>
            </div>
          </div>
          <div class="wcag-cat__bar">
            <div
              class="wcag-cat__bar-fill"
              :class="`wcag-cat__bar-fill--${cat.color}`"
              :style="{
                width: cat.max
                  ? Math.round((cat.score / cat.max) * 100) + '%'
                  : '0%'
              }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Failures — surfaces the actionable items first. -->
      <div v-if="failures.length" class="wcag-modal__checklist">
        <h3 class="wcag-modal__section-title">
          <i
            class="fas fa-times-circle"
            style="color: #dc2626; margin-right: 6px"
          ></i>
          {{
            $t('Components.WcagReportModal.failedChecks', {
              count: failures.length
            })
          }}
        </h3>
        <div
          v-for="item in failures"
          :key="`fail-${item.id}`"
          class="wcag-criteria wcag-criteria--fail"
        >
          <div class="wcag-criteria__status wcag-criteria__status--fail">
            <i class="fas fa-times" aria-hidden="true"></i>
          </div>
          <div class="wcag-criteria__body">
            <div class="wcag-criteria__id">{{ item.wcagId || item.id }}</div>
            <div class="wcag-criteria__name">{{ item.help }}</div>
            <div class="wcag-criteria__detail">
              {{
                $tc('Components.WcagReportModal.elementsAffected', item.nodes, {
                  n: item.nodes
                })
              }}
              · {{ $t('Components.WcagReportModal.impact') }}:
              <strong>{{ impactLabel(item.impact) }}</strong>
            </div>
            <a
              v-if="item.helpUrl"
              :href="item.helpUrl"
              target="_blank"
              rel="noopener"
              class="wcag-criteria__link"
              >{{ $t('Components.WcagReportModal.learnMore') }}
              <i class="fas fa-external-link-alt"></i
            ></a>
          </div>
          <span class="wcag-criteria__level">{{ item.level }}</span>
        </div>
      </div>

      <!-- Needs Review — incomplete checks (axe couldn't auto-decide). -->
      <div v-if="reviewItems.length" class="wcag-modal__checklist">
        <h3 class="wcag-modal__section-title">
          <i class="fas fa-eye" style="color: #d97706; margin-right: 6px"></i>
          {{
            $t('Components.WcagReportModal.needsReview', {
              count: reviewItems.length
            })
          }}
        </h3>
        <div
          v-for="item in reviewItems"
          :key="`rev-${item.id}`"
          class="wcag-criteria wcag-criteria--review"
        >
          <div class="wcag-criteria__status wcag-criteria__status--partial">
            <i class="fas fa-exclamation" aria-hidden="true"></i>
          </div>
          <div class="wcag-criteria__body">
            <div class="wcag-criteria__id">{{ item.wcagId || item.id }}</div>
            <div class="wcag-criteria__name">{{ item.help }}</div>
            <div class="wcag-criteria__detail">
              {{
                $tc(
                  'Components.WcagReportModal.elementsRequireReview',
                  item.nodes,
                  { n: item.nodes }
                )
              }}
            </div>
          </div>
          <span class="wcag-criteria__level">{{ item.level }}</span>
        </div>
      </div>

      <!-- Passed — compact list so the report shows progress. Hidden
           behind a disclosure to keep the report scannable. -->
      <details
        v-if="passes.length"
        class="wcag-modal__checklist wcag-modal__passes"
      >
        <summary class="wcag-modal__section-title">
          <i
            class="fas fa-check-circle"
            style="color: #15803d; margin-right: 6px"
          ></i>
          {{
            $t('Components.WcagReportModal.passedChecks', {
              count: passes.length
            })
          }}
        </summary>
        <div
          v-for="item in passes"
          :key="`pass-${item.id}`"
          class="wcag-criteria wcag-criteria--pass"
        >
          <div class="wcag-criteria__status wcag-criteria__status--pass">
            <i class="fas fa-check" aria-hidden="true"></i>
          </div>
          <div class="wcag-criteria__body">
            <div class="wcag-criteria__id">{{ item.wcagId || item.id }}</div>
            <div class="wcag-criteria__name">{{ item.help }}</div>
          </div>
          <span class="wcag-criteria__level">{{ item.level }}</span>
        </div>
      </details>
    </template>

    <div class="wcag-modal__footer">
      <button
        v-if="state === 'ready'"
        class="wcag-modal__btn wcag-modal__btn--secondary"
        @click="runAudit"
      >
        <i class="fas fa-redo-alt"></i>
        {{ $t('Components.WcagReportModal.rerun') }}
      </button>
      <button class="wcag-modal__btn" @click="onClose">
        {{ $t('Views.Stage.ParticipantsModal.close') }}
      </button>
    </div>
  </div>
</template>

<script>
// Maps axe-core's `tags` array onto WCAG criterion data so the
// existing categorized UI keeps working. axe rules carry tags like
//   'wcag2a', 'wcag2aa', 'wcag21aa', 'wcag22aa', 'wcag143',
//   'cat.color-and-contrast', 'cat.aria', etc.
//
// `wcagXYZ` / `wcagXYZW` encodes the principle.guideline.criterion as
// concatenated digits: wcag143 → 1.4.3, wcag1411 → 1.4.11.

// Each WCAG principle (top-level numeral) maps to a category name +
// theme color matching the original report's chrome.
const PRINCIPLE_META = {
  // `name` is an identifier; `key` selects the translated display label.
  1: {
    name: 'Perceivable',
    key: 'perceivable',
    color: 'teal',
    icon: 'far fa-eye'
  },
  2: {
    name: 'Operable',
    key: 'operable',
    color: 'blue',
    icon: 'far fa-hand-pointer'
  },
  3: {
    name: 'Understandable',
    key: 'understandable',
    color: 'purple',
    icon: 'far fa-lightbulb'
  },
  4: {
    name: 'Robust',
    key: 'robust',
    color: 'orange',
    icon: 'fas fa-shield-alt'
  }
};

// Pulls the WCAG criterion (e.g. '1.4.3') and conformance level
// (e.g. 'AA') out of a rule's `tags` array.
function parseWcagFromTags(tags) {
  if (!Array.isArray(tags)) return { criterion: null, level: null };
  let criterion = null;
  let level = null;
  for (const t of tags) {
    // wcag111, wcag143, wcag1411, etc.
    const m = /^wcag(\d{3,4})$/.exec(t);
    if (m && !criterion) {
      const digits = m[1];
      if (digits.length === 3) {
        criterion = `${digits[0]}.${digits[1]}.${digits[2]}`;
      } else {
        // 4-digit form: first two digits are principle.guideline,
        // remaining two digits are the criterion (e.g. 1411 → 1.4.11).
        criterion = `${digits[0]}.${digits[1]}.${digits.slice(2)}`;
      }
    }
    // wcag2a / wcag2aa / wcag21a / wcag21aa / wcag22a / wcag22aa
    const lvl = /^wcag\d{2,3}(a{1,3})$/.exec(t) || /^wcag2(a{1,3})$/.exec(t);
    if (lvl && !level) {
      level = lvl[1].toUpperCase();
    }
  }
  return { criterion, level };
}

// Maps a parsed criterion ID like '1.4.3' to its principle bucket.
function principleOf(criterion) {
  if (!criterion) return null;
  const first = criterion.split('.')[0];
  return PRINCIPLE_META[first] || null;
}

import { intlLocale } from '@/i18n';

export default {
  name: 'WcagReportModal',
  data() {
    return {
      state: 'loading', // 'loading' | 'ready' | 'error'
      errorMessage: '',
      auditDate: new Date().toLocaleDateString(intlLocale(), {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      auditedRoute: '',
      // Normalized rule results: each entry is
      // { id, wcagId, level, category, help, helpUrl, impact, nodes, status }
      results: []
    };
  },
  created() {
    this.runAudit();
  },
  computed: {
    failures() {
      return this.results.filter((r) => r.status === 'fail');
    },
    reviewItems() {
      return this.results.filter((r) => r.status === 'review');
    },
    passes() {
      return this.results.filter((r) => r.status === 'pass');
    },
    stats() {
      return {
        pass: this.passes.length,
        fail: this.failures.length,
        review: this.reviewItems.length,
        total: this.results.length
      };
    },
    score() {
      // Deterministic score = passes / (passes + fails) × 100.
      // "Needs review" (incomplete) rules are excluded from the
      // denominator entirely — they're indeterminate by axe's own
      // admission, so counting them either way creates the 88→91
      // run-to-run wobble. Now the denominator only includes rules
      // with a definite outcome on this page, giving a stable score
      // for the same DOM snapshot. Rounded HALF-UP for consistency.
      const decided = this.stats.pass + this.stats.fail;
      if (!decided) return 100;
      const raw = (this.stats.pass / decided) * 100;
      return Math.floor(raw + 0.5);
    },
    scoreBadge() {
      if (this.score >= 90) {
        return {
          label: this.$t('Components.WcagReportModal.badgeGood'),
          tone: 'pass',
          icon: 'fas fa-check-circle'
        };
      }
      if (this.score >= 70) {
        return {
          label: this.$t('Components.WcagReportModal.badgeNeedsWork'),
          tone: 'warn',
          icon: 'fas fa-exclamation-circle'
        };
      }
      return {
        label: this.$t('Components.WcagReportModal.badgeSignificant'),
        tone: 'fail',
        icon: 'fas fa-times-circle'
      };
    },
    ringColor() {
      if (this.scoreBadge.tone === 'pass')
        return 'var(--color-primary, #047873)';
      if (this.scoreBadge.tone === 'warn') return '#d97706';
      return '#dc2626';
    },
    categories() {
      // Group all checked criteria into the four WCAG principles.
      // Each row shows passed-vs-total so principle-level progress
      // is visible at a glance.
      const buckets = new Map();
      this.results.forEach((r) => {
        if (!r.category) return;
        if (!buckets.has(r.category.name)) {
          buckets.set(r.category.name, {
            ...r.category,
            score: 0,
            max: 0
          });
        }
        const b = buckets.get(r.category.name);
        b.max += 1;
        if (r.status === 'pass') b.score += 1;
      });
      return ['Perceivable', 'Operable', 'Understandable', 'Robust']
        .filter((name) => buckets.has(name))
        .map((name) => buckets.get(name));
    },
    circumference() {
      return 2 * Math.PI * 52;
    },
    dashOffset() {
      return this.circumference * (1 - this.score / 100);
    }
  },
  methods: {
    // Injects axe-core via a <script> tag if it isn't already on the
    // global. Caches the in-flight promise so re-running the audit
    // doesn't double-load. Resolves with the global `axe` object.
    loadAxeCore() {
      if (typeof window === 'undefined') {
        return Promise.reject(new Error('No window object available.'));
      }
      if (window.axe) return Promise.resolve(window.axe);
      if (window.__axeCorePromise) return window.__axeCorePromise;

      window.__axeCorePromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        // Pinned to a known-good major + minor; the runtime ships a
        // sub-resource integrity hash because axe-core is third-party
        // code that touches the DOM extensively. If the integrity
        // check fails the browser refuses to execute it, which fails
        // the load cleanly back to our error state.
        script.src = 'https://cdn.jsdelivr.net/npm/axe-core@4.10.0/axe.min.js';
        script.async = true;
        script.crossOrigin = 'anonymous';
        script.referrerPolicy = 'no-referrer';
        script.onload = () => {
          if (window.axe) {
            resolve(window.axe);
          } else {
            reject(new Error('axe-core loaded but the global was not set.'));
          }
        };
        script.onerror = () => {
          // Clear the cached promise so a re-run from the modal's
          // retry button doesn't get stuck on the failed state.
          window.__axeCorePromise = null;
          reject(
            new Error(this.$t('Components.WcagReportModal.cdnFetchFailed'))
          );
        };
        document.head.appendChild(script);
      });

      return window.__axeCorePromise;
    },

    async runAudit() {
      this.state = 'loading';
      this.errorMessage = '';
      this.results = [];
      // Capture the route name so the report can label the page it
      // audited — different pages legitimately produce different
      // scores, and the user should see which one this is.
      this.auditedRoute = (this.$route && this.$route.name) || '';

      let axe;
      try {
        axe = await this.loadAxeCore();
      } catch (e) {
        this.state = 'error';
        this.errorMessage =
          (e && e.message) ||
          this.$t('Components.WcagReportModal.engineLoadFailed');
        return;
      }

      // ── Stabilise the DOM before scanning ───────────────────────
      // The 88→91 variance between re-runs comes from transient DOM
      // state (focus rings, tooltips mid-fade, async-loaded content,
      // "incomplete" rules that flip between pass/fail on borderline
      // cases). Waiting for two animation frames plus a short delay
      // gives Vue and the browser time to settle so the audit
      // measures a stable snapshot.
      await new Promise((resolve) => {
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() =>
            requestAnimationFrame(() => setTimeout(resolve, 250))
          );
        } else {
          setTimeout(resolve, 350);
        }
      });

      try {
        const report = await axe.run(document, {
          runOnly: {
            type: 'tag',
            values: [
              'wcag2a',
              'wcag2aa',
              'wcag21a',
              'wcag21aa',
              'wcag22a',
              'wcag22aa'
            ]
          },
          resultTypes: ['violations', 'passes', 'incomplete']
        });

        const normalized = [];
        const pushRule = (rule, status) => {
          const parsed = parseWcagFromTags(rule.tags);
          normalized.push({
            id: rule.id,
            wcagId: parsed.criterion,
            level: parsed.level || '',
            category: principleOf(parsed.criterion),
            help: rule.help || rule.description || rule.id,
            helpUrl: rule.helpUrl || '',
            impact:
              rule.impact ||
              (rule.nodes && rule.nodes[0] && rule.nodes[0].impact) ||
              null,
            nodes: Array.isArray(rule.nodes) ? rule.nodes.length : 0,
            status
          });
        };
        (report.violations || []).forEach((r) => pushRule(r, 'fail'));
        (report.incomplete || []).forEach((r) => pushRule(r, 'review'));
        (report.passes || []).forEach((r) => pushRule(r, 'pass'));

        // Stable sort: failures first by impact (critical > serious >
        // moderate > minor > null), then incomplete, then passes.
        const impactRank = {
          critical: 0,
          serious: 1,
          moderate: 2,
          minor: 3,
          null: 4
        };
        normalized.sort((a, b) => {
          const sa = { fail: 0, review: 1, pass: 2 }[a.status];
          const sb = { fail: 0, review: 1, pass: 2 }[b.status];
          if (sa !== sb) return sa - sb;
          const ia = impactRank[a.impact || 'null'] ?? 4;
          const ib = impactRank[b.impact || 'null'] ?? 4;
          return ia - ib;
        });

        this.results = normalized;
        this.state = 'ready';
      } catch (e) {
        this.state = 'error';
        this.errorMessage = this.$t('Components.WcagReportModal.scanFailed', {
          error: e && e.message ? e.message : String(e)
        });
      }
    },
    impactLabel(impact) {
      const key = `Components.WcagReportModal.impacts.${impact || 'unknown'}`;
      return this.$te(key) ? this.$t(key) : impact;
    },
    onClose() {
      document.body.classList.remove('overflow-hidden');
      this.$emit('close');
    }
  }
};
</script>

<style scoped lang="scss">
.wcag-modal {
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  padding: 32px 24px 24px;
  font-family: 'Poppins', sans-serif;
  max-height: 90vh;
  overflow-y: auto;

  &__close {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: #f3f4f6;
    color: #374151;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    transition: background 0.15s;

    &:hover {
      background: #e5e7eb;
    }
  }

  &__header {
    text-align: center;
    margin-bottom: 18px;
  }

  &__icon-wrap {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #e6f5f4;
    color: var(--color-primary, #047873);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    margin: 0 auto 14px;
    box-shadow: 0 0 0 8px rgba(4, 120, 115, 0.08);
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 4px;
  }

  &__sub {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
  }

  // Disclaimer banner
  &__disclaimer {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    margin-bottom: 22px;
    background: #fef3c7;
    border: 1px solid #fde68a;
    border-left: 4px solid #d97706;
    border-radius: 10px;
    color: #78350f;
    font-size: 12px;
    line-height: 1.55;
  }

  &__disclaimer-icon {
    color: #d97706;
    font-size: 16px;
    flex-shrink: 0;
    margin-top: 1px;
  }

  &__disclaimer-body {
    strong {
      display: block;
      font-weight: 700;
      color: #78350f;
      margin-bottom: 4px;
    }
  }

  // Loading state
  &__loading {
    text-align: center;
    padding: 60px 20px;
    color: var(--color-primary, #047873);

    i {
      font-size: 32px;
      margin-bottom: 14px;
    }

    p {
      margin: 0;
      font-size: 13px;
      color: #6b7280;
    }
  }

  // Error state
  &__error {
    text-align: center;
    padding: 30px 20px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 12px;
    margin-bottom: 20px;

    i {
      font-size: 32px;
      color: #dc2626;
      margin-bottom: 10px;
    }
  }

  &__error-title {
    font-size: 15px;
    font-weight: 700;
    color: #991b1b;
    margin: 0 0 8px;
  }

  &__error-detail {
    font-size: 12px;
    color: #7f1d1d;
    margin: 0 0 14px;
    line-height: 1.5;
  }

  &__error-hint {
    font-size: 12px;
    color: #7f1d1d;
    margin: 0;
    line-height: 1.5;

    code {
      background: #fee2e2;
      padding: 2px 8px;
      border-radius: 6px;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 11px;
      color: #991b1b;
    }
  }

  // ── Score ring ──────────────────────────────────────────────────
  &__score-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 24px;
  }

  &__ring {
    position: relative;
    width: 140px;
    height: 140px;
    margin-bottom: 14px;
  }

  &__ring-svg {
    width: 100%;
    height: 100%;
  }

  &__ring-progress {
    transition: stroke-dashoffset 1s ease-out;
  }

  &__ring-label {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  &__ring-num {
    font-size: 36px;
    font-weight: 700;
    color: #111827;
    line-height: 1;
  }

  &__ring-max {
    font-size: 13px;
    color: #6b7280;
    font-weight: 500;
  }

  &__score-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
  }

  &__score-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 700;

    &--pass {
      background: #dcfce7;
      color: #15803d;
    }

    &--warn {
      background: #fef3c7;
      color: #b45309;
    }

    &--fail {
      background: #fee2e2;
      color: #991b1b;
    }
  }

  &__score-date {
    font-size: 12px;
    color: #6b7280;
  }

  &__score-caption {
    margin: 0;
    font-size: 12px;
    color: #6b7280;
    text-align: center;

    strong {
      color: #111827;
      font-weight: 700;
    }
  }

  // ── Category bars ───────────────────────────────────────────────
  &__categories {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 28px;
  }

  &__section-title {
    font-size: 13px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    cursor: default;
    list-style: none;

    &::-webkit-details-marker {
      display: none;
    }
  }

  // ── Checklist ───────────────────────────────────────────────────
  &__checklist {
    margin-bottom: 20px;
  }

  &__passes {
    summary {
      cursor: pointer;
    }
  }

  &__footer {
    display: flex;
    justify-content: center;
    gap: 10px;
    border-top: 1px solid #f3f4f6;
    padding-top: 18px;
    flex-wrap: wrap;
  }

  &__btn {
    min-height: 40px;
    padding: 10px 22px;
    background: var(--color-primary, #047873);
    color: #ffffff;
    border: none;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'Poppins', sans-serif;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: background 0.15s;

    &:hover {
      background: var(--color-primary-hover, #036663);
    }

    &--secondary {
      background: #f3f4f6;
      color: #374151;

      &:hover {
        background: #e5e7eb;
      }
    }

    i {
      font-size: 12px;
    }
  }
}

// ── Category card ─────────────────────────────────────────────────
.wcag-cat {
  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;

    &--teal {
      background: #e6f5f4;
      color: var(--color-primary, #047873);
    }
    &--blue {
      background: #dbeafe;
      color: #1d4ed8;
    }
    &--purple {
      background: #f3e8ff;
      color: #7e22ce;
    }
    &--orange {
      background: #fef3c7;
      color: #d97706;
    }
  }

  &__info {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__name {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
  }

  &__score {
    font-size: 13px;
    font-weight: 700;
    color: #374151;
  }

  &__bar {
    height: 8px;
    background: #f3f4f6;
    border-radius: 9999px;
    overflow: hidden;
  }

  &__bar-fill {
    height: 100%;
    border-radius: 9999px;
    transition: width 0.8s ease-out;

    &--teal {
      background: var(--color-primary, #047873);
    }
    &--blue {
      background: #3b82f6;
    }
    &--purple {
      background: #a855f7;
    }
    &--orange {
      background: #f59e0b;
    }
  }
}

// ── Criteria row ──────────────────────────────────────────────────
.wcag-criteria {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }

  &__status {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex-shrink: 0;
    margin-top: 2px;

    &--pass {
      background: #dcfce7;
      color: #15803d;
    }

    &--partial {
      background: #fef3c7;
      color: #d97706;
    }

    &--fail {
      background: #fee2e2;
      color: #dc2626;
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__id {
    font-size: 11px;
    font-weight: 700;
    color: #6b7280;
    letter-spacing: 0.02em;
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    line-height: 1.35;
  }

  &__detail {
    font-size: 11px;
    color: #6b7280;
    margin-top: 2px;
    line-height: 1.4;

    strong {
      color: #374151;
      font-weight: 600;
      text-transform: capitalize;
    }
  }

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: var(--color-primary, #047873);
    text-decoration: none;
    margin-top: 4px;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }

    i {
      font-size: 9px;
    }
  }

  &__level {
    font-size: 10px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 6px;
    background: #f3f4f6;
    color: #374151;
    flex-shrink: 0;
    margin-top: 2px;
  }
}
</style>
